/**
 * 多人拼单
 */
import { makeAutoObservable, autorun, runInAction } from 'mobx'
import { restaurantConfig, cartMobx, storeMobx, restaurantCenter, RESTAURANT_EVENT, doProductMobx, doComboMobx, orderMobx, productMobx } from 'restaurant'
import { themeMobx, deliveryMobx, cartLiveMobx, multiPersonMobx, multiPersonOrderingMobx, memberMobx } from '@mobxStore'
import { createFkey, convertDoModifierGroupsToModifierGroups } from 'restaurant/mobx/center/cookware'
import { createCartItem } from 'restaurant/mobx/cart.mobx'
import { TEEatType } from 'restaurant/mobx/store.mobx'
import { TEnumUserStatus } from '@mobx/cashier/multiPerson.mobx'
import { getHistoryNew, alertLayer, modalLayer, getPlatform, miniAppRouteTo, showMsg, getEnv } from '@utils'
import { ddayjs, getParams, deepClone, toDecimal } from '@dt/fe-utils'
import { concat, findLast, keyBy, find, forEach, groupBy, mapValues, sumBy } from '@dt/fe-utils/dist/lodash'
import { DeliveryController, CashierController } from '@ctrls/index'
import { MoApi, MultiPersonOrderingAPI } from '@api/index'
import { SelectAddressModal } from '@cps'
const { isDebug } = getEnv()

export enum TSaveCartError {
  OVER_LIMIT = 'OVER_LIMIT',
  DATA_ERROR = 'DATA_ERROR'
}
interface IMultiSelectData {
  [eatType: string]: {
    store: any;
  };
}

// 功能入口
interface IBTEntry extends IEntry {
  disabled?: boolean;
}

const getEatType = (eatType) => eatType === TEEatType.delivery ? TEEatType.delivery : TEEatType.pickup

class BuyTogether {
  constructor () {
    makeAutoObservable(this)
    restaurantCenter.on(RESTAURANT_EVENT.STORE_CHANGED, (info: IStore) => {
      this.hasInit = false
      this.setStore(info, info.openSelfDelivery === 1 ? TEEatType.delivery : TEEatType.pickup)
    })
  }

  defaultEntries: IBTEntry[] = [
    {
      icon: 'entry-newpickup0',
      mode: 0,
      name: i18n.tbo('menu.buyTogether.btn_pickup'),
      pic: '',
      type: TEEatType.pickup,
      disabled: false,
    },
    {
      icon: 'entry-newdelivery1',
      mode: 0,
      name: i18n.tbo('menu.buyTogether.btn_delivery'),
      pic: '',
      type: TEEatType.delivery,
      disabled: false,
    }
  ]
  defaultBtEntrie: IBTEntry = {
    icon: 'entry-newbuyTogether1',
    mode: 0,
    name: i18n.tbo('menu.buyTogether.entryName'),
    pic: '',
    type: 'buyTogether',
  }
  isInit: boolean = false
  /** 拼单id */
  buyTogetherId: string = '' // bt_${Config.BRANDID}_${multiPersonMobx.currentPersonInfo.personId}_${new Date().getTime()}
  /** 结算页必选提示卡片加购后，立即把临时餐品合并回拼单购物车。 */
  syncCartFormMenuProductNo = ''
  /** 拼单方式 */
  eatType: TEntryType | undefined = TEEatType.delivery
  /** 自提、外卖icon name 装修数据 */
  btEatEntries: IBTEntry[] = this.defaultEntries
  // 拼单icon name 装修数据
  btEntrie: IBTEntry = this.defaultBtEntrie
  /** 选中的门店 */
  storeInfo: IMultiSelectData = { pickup: { store: {} }, delivery: { store: {} } }
  /** 当前选中的地址 */
  addressInfo: any = {}
  isOpen: boolean = false
  /** 保存临时购物车数据，用于点餐保存 */
  cartFormMenu: IViewCartItem[] = []
  /** 用户点餐状态 */
  useStatus: any = 'init'
  hasInit: boolean = false

  memberMap = {}

  /** 拼单发起弹窗获取店铺数据keyMap */
  get storeInfoByType () {
    return this.storeInfo[this.eatType]
  }

  /** 是否有开通拼单的门店 */
  get hasOpenBTStore () {
    return !!storeMobx.viewStoreList.find((store) => store.buyTogetherEatTypes.length > 0)
  }

  /** 是否有开通拼单外卖的门店 */
  get hasOpenBTDeliveryStore () {
    return !!storeMobx.viewStoreList.find((store) => store.buyTogetherEatTypes.includes(TEEatType.delivery))
  }

  /** 是否有开通拼单自提的门店 */
  get hasOpenBTPickUpStore () {
    return !!storeMobx.viewStoreList.find((store) => store.buyTogetherEatTypes.includes(TEEatType.pickup))
  }

  /** 是否已经可以前往拼单 */
  get isReadyToStart () {
    return !!storeMobx.viewStoreList.find((store) => store.buyTogetherEatTypes.includes(this.eatType))
  }
  /** 已点餐品的数量map,用于点餐保存 */
  get productNoToCntMapForMenu () {
    return mapValues(groupBy(this.cartFormMenu, 'productNo'), (group) => sumBy(group, 'cnt'))
  }

  /** 自动打开拼单发起弹窗 */
  get canAutoStartOpen () {
    const { tt: targetType = '' } = getParams()
    if (targetType === 'buyTogether' && this.canStartBuyTogether) {
      this.setOpen(true)
      return true
    }
    return false
  }

  /** 判断是否显示拼单按钮 是否可以发起拼单 */
  get canStartBuyTogether () {
    const { isWeixinMiniProgram } = getPlatform()
    return isDebug || (bo === 'cn' && !Config.buyTogetherId && isWeixinMiniProgram && Config.codeType === 'brand' && storeMobx.info.isOpenBuyTogether)
  }

  /** 地址列表 */
  get addressList () {
    return deepClone(memberMobx.addressList || [])
  }

  /** 可用地址列表 */
  get viewAddressList () {
    const storeInfo = this.storeInfoByType?.store || {}
    const addressList = this.addressList
    const viewAddresses = deliveryMobx.verifyAddresses(addressList, storeInfo)
    return viewAddresses
  }

  /** 是否授权微信头像 */
  get checkNotAuthAvatarCanBT () {
    const { isWeixinMiniProgram } = getPlatform()
    if (isWeixinMiniProgram) {
      if (!memberMobx.memberInfo.memberId || !memberMobx.memberInfo.avatarUrl) {
        miniAppRouteTo({ url: `/pages/authAvatar/index?jumpH5Page=${encodeURIComponent(location.href)}` })
        return true
      }
    }
    return false
  }

  /** 获取我的餐品 productNo为key统计加入购物车的数量 */
  get noToMeCartItemsCntMap () {
    const map: any = {}
    forEach((cartMobx.viewUserCartItems[multiPersonMobx.currentPersonInfo.personId] || []), (item) => {
      map[item.productNo] = (map[item.productNo] || 0) + item.cnt
    })
    return map
  }

  /** 获取房间所有者 */
  get getOwner () {
    const ownerId = this.buyTogetherId?.split('_')[2]
    return ownerId
  }

  /** 计算后的购物概述，包括数量、价格等 */
  get cartMenuTotal () {
    const productNoToCntMap: any = {}
    const scaleNoToCntMap: {[pscaleNo: string]: number} = {}
    // (餐品 + 服务费 + 打包费 + 配送费 + 税 + 小费 - 优惠）
    let totalPrice = 0
    let totalCartPrice = 0
    let totalCartBoxPrice = 0
    // 当次购物车中的餐品总价
    let presentItemPrice = 0
    // 餐品原价
    let itemPrice = 0
    // 打包费
    let boxPrice = 0
    // 当前购物车餐品总数
    let presentTotalCnt = 0
    // 当前购物车餐品 + 已下订单中餐品的总数
    let totalCnt = 0
    this.cartFormMenu.filter(item => item.cnt && !item.disabled).forEach(item => {
      totalCnt += item.disabled ? 0 : item.cnt
      presentTotalCnt += item.disabled ? 0 : item.cnt
      // 手机: 换购、满送餐品不计入菜单已点数量 20211122
      if (!(restaurantCenter.config.app === 'mobile')) {
        productNoToCntMap[item.productNo] = (productNoToCntMap[item.productNo] || 0) + item.cnt
      }
      scaleNoToCntMap[item.productNo + '_' + item.scaleNo] = (scaleNoToCntMap[item.productNo + '_' + item.scaleNo] || 0) + item.cnt
      if (item.disabled) {
        return
      }
      item.totalPrice = item.totalPrice || item.price
      boxPrice += item.totalBoxPrice || 0
      presentItemPrice += item.totalPrice || 0
      totalPrice += item.totalPrice + item.totalBoxPrice || 0
      itemPrice += (item.productPrice || item.price) * item.cnt
      totalCartBoxPrice += item.totalBoxPrice
      totalCartPrice += item.totalPrice + item.totalBoxPrice
    })
    return {
      totalCnt,
      productNoToCntMap,
      scaleNoToCntMap,
      itemTotalPrice: toDecimal(itemPrice),
    }
  }

  /** productNo为key统计加入购物车的数量 */
  get noToCartItemsCntMap () {
    const map: any = {}
    forEach(this.cartFormMenu, (item) => {
      map[item.productNo] = (map[item.productNo] || 0) + item.cnt
    })
    return map
  }

  /** 开启拼单初始化 from 来源 */
  async initStart (from?: string) {
    if (!this.hasInit) {
      if (from !== 'btn' && this.hasOpenBTDeliveryStore) {
        this.setEatType(TEEatType.delivery)
        // cartMobx.setEatType(TEEatType.delivery)
      } else {
        this.setEatType(TEEatType.pickup)
      }
      this.storeInfo = {
        pickup: {
          store: storeMobx.viewStoreList.find(item => item.buyTogetherEatTypes.includes(TEEatType.pickup)) || {}
        },
        delivery: {
          store: storeMobx.viewStoreList.find(item => item.buyTogetherEatTypes.includes(TEEatType.delivery)) || {}
        }
      }
      if (!deliveryMobx.hasAddresses) await memberMobx.getMemberAddress()
      let addressListAll = memberMobx.addressList
      let list = deliveryMobx.verifyAddresses(addressListAll, this.storeInfo.delivery.store).filter((item) => item.inCityId && item.inArea)
      // this.addressInfo = list[0] || {}
      const address = list[0] || {}
      this.setAddress(address)
      multiPersonMobx.init({}, {})
      this.hasInit = true
    }
  }

  /** 拼单购物车初始化 */
  async initCart () {
    if (this.isInit) return
    if (!(Config.buyTogetherId && restaurantConfig.member.id)) return
    if (this.getOwner !== String(memberMobx.memberInfo?.memberId) && this.checkNotAuthAvatarCanBT) return
    let storageData = this.getStorage()
    const { buyTogetherId } = Config
    this.buyTogetherId = buyTogetherId
    const hasStorageData = storageData && storageData.buyTogetherId === Config.buyTogetherId
    if (hasStorageData) {
      let { storeInfo, addressInfo, eatType } = storageData
      this.setStore(storeInfo)
      this.setAddress(addressInfo)
      if (!deliveryMobx.hasAddresses) await memberMobx.getMemberAddress()
      storageData && deliveryMobx.setAddress(storageData)
      if (eatType) {
        this.setEatType(eatType)
        CashierController.switchEatType(eatType)
      }
    }
    multiPersonOrderingMobx.connectTogether(this.buyTogetherId, (data: any) => {
      cartLiveMobx.init(data)
      multiPersonMobx.init(data?.user, data?.person)
      const ownerId = this.getOwner
      // 发起人数据
      const { ordersId = '', eatType = TEEatType.pickup, address = {} } = data?.user?.[ownerId] || { ordersId: '', eatType: TEEatType.pickup, address: {} }
      // 本地缓存没有拼单数据，从云端购物车取
      if (data?.user?.[ownerId] && !hasStorageData) {
        this.setEatType(eatType)
        CashierController.switchEatType(eatType as TEatType)
        this.setStorage({
          buyTogetherId: this.buyTogetherId,
          eatType: eatType || TEEatType.pickup,
          storeInfo: storeMobx.info,
          addressInfo: address,
        })
      }
      if (ordersId) {
        orderMobx.init(ordersId)
      }
    })
    this.isInit = true
  }

  /** 设置拼单按钮装修数据 */
  setEntries () {
    const { homeDecorates } = themeMobx.brandTemplateDetails
    const entries = (homeDecorates.entrySetting?.brandCode?.entries || [])
    runInAction(() => {
      this.btEatEntries = this.defaultEntries.map((entrie) => {
        return {
          ...entries.find(item => item.type === entrie.type) || { ...entrie, name: entrie.name || i18n.t(`menu.buyTogether.btn_${entrie.type}`) },
          disabled: (entrie.type === TEEatType.pickup && !this.hasOpenBTPickUpStore) || (entrie.type === TEEatType.delivery && !this.hasOpenBTDeliveryStore)
        }
      })
      this.btEntrie = entries.find(item => item.type === 'buyTogether') || this.defaultBtEntrie
    })
  }

  /** 切换设置拼单eatType */
  setEatType (type) {
    this.eatType = getEatType(type)
  }

  /** 切换地址 */
  setAddress (data: any) {
    if (data) {
      this.addressInfo = data.mobile ? data : {}
    }
  }
  /** 切换门店 */
  setStore (data, type?: TEatType) {
    let obj = this.storeInfo
    obj[getEatType(type || this.eatType)].store = data
    this.storeInfo = { ...obj }
  }

  /** 选择收货地址 */
  selectAddress () {
    // const { verifyAddresses } = deliveryMobx
    if (this.viewAddressList?.length) {
      const inAreaAddressList = this.viewAddressList.filter(ad => ad.inArea) || []
      if (!this.addressInfo?.mobile && inAreaAddressList.length) {
        this.setAddress(inAreaAddressList[0])
      }
      modalLayer(SelectAddressModal, { type: 'buyTogether' }, 'SelectAddressModal').then((address: any) => {
        this.checkAvailableAddress(address)
      }, () => { })
    } else {
      DeliveryController.editAddressModal({
        type: 'buyTogether',
        callback: async (address) => {
          this.checkAvailableAddress(address)
        }
      })
    }
  }

  /** 查找离门店最近的地址 */
  checkAvailableAddress (address) {
    let newAddress = deliveryMobx.verifyAddresses(address ? [address] : [], this.storeInfo.delivery.store)?.[0]
    if (newAddress && newAddress.inArea) {
      this.setAddress(newAddress)
    } else {
      alertLayer({
        header: i18n.t('common.reminder'),
        message: i18n.t('newCart.noMatchAddress'),
        button: i18n.t('newCart.confirm')
      }).then(() => {
      })
    }
  }
  /** 检查最大限售数量 当前加购数量是否超过限售数量 */
  checkCartItemMaxSalesCnt () {
    const product = doProductMobx.doProduct
    const maxSalesCnt = product.maxSalesCnt || 0
    if (maxSalesCnt) {
      const menuCartCnt = this.noToCartItemsCntMap[product.no] || 0
      const cartCnt = cartMobx.noToCartItemsCntMap[product.no] || 0
      // 购物车内我点的当前餐品的数量
      const meCnt = this.noToMeCartItemsCntMap[product.no] || 0
      const cnt = product.cnt
      if (cnt + cartCnt + menuCartCnt - meCnt > maxSalesCnt) {
        return maxSalesCnt
      }
    }
    return 0
  }

  /** 检查购物车最大限售数量 当前加购数量是否超过限售数量 */
  checkCartItemsMaxSalesCnt (products: IViewCartItem[]): IViewProduct {
    const productMap = productMobx.noToViewProductMap
    let hasCartItem: IViewProduct
    find(products, (item: IViewCartItem) => {
      const product = productMap[item.productNo]
      const maxSalesCnt = product?.maxSalesCnt || 0
      if (maxSalesCnt) {
        // 购物车当前餐品的总数量
        const cartCnt = cartMobx.noToCartItemsCntMap[product.no] || 0
        // 购物车内我点的当前餐品的数量
        const meCnt = this.noToMeCartItemsCntMap[product.no] || 0
        const cnt = item.cnt
        if ((cnt + cartCnt - meCnt) > maxSalesCnt) {
          hasCartItem = product
          return true
        }
      }
    })
    return hasCartItem
  }

  /** 验证是否可以加入购物车 */
  checkAddCart (cartItems?: IViewCartItem[]) {
    const maxSalesCntProduct: IViewProduct = this.checkCartItemsMaxSalesCnt(cartItems || this.cartFormMenu)
    if (maxSalesCntProduct) {
      showMsg(i18n.t('cart.btMaxSalesCnt', { name: maxSalesCntProduct.name, cnt: maxSalesCntProduct.maxSalesCnt }), 2000)
      return true
    }
    return false
  }

  /** 创建房间 */
  async createRoom (type?: string) {
    if (this.eatType === TEEatType.delivery) {
      if (!this.addressInfo.mobile) {
        this.selectAddress()
        return
      }
      let isInArea = false
      if (this.viewAddressList?.length) {
        isInArea = this.viewAddressList.filter(adr => adr?.memberAddressId === this.addressInfo?.memberAddressId)?.[0]?.inArea || false
      }

      if (!this.viewAddressList?.length || this.viewAddressList?.length && !isInArea) {
        this.setAddress({})
        alertLayer({
          header: i18n.t('common.reminder'),
          message: i18n.t('newCart.noMatchAddress'),
          button: i18n.t('newCart.confirm')
        }).then(() => {
        })

        return
      }
    }
    multiPersonMobx.initBTPersonInfo()
    if (!this.buyTogetherId) {
      this.buyTogetherId = `bt_${Config.BRANDID}_${multiPersonMobx.currentPersonInfo.personId}_${new Date().getTime()}`
    }
    this.goToRoom(type)
  }

  /** 跳转拼单购物车 */
  goToRoom (type?: string) {
    const history = getHistoryNew()
    if (this.buyTogetherId) {
      const storeInfo = type === 'store' ? storeMobx.info : this.storeInfoByType.store
      this.setStorage({
        buyTogetherId: this.buyTogetherId,
        eatType: this.eatType,
        storeInfo,
        addressInfo: this.addressInfo,
      })
      location.href = `/buyTogetherCart?ct=brand&b=${restaurantConfig.brandId}&s=${storeInfo.storeId || 0}&bt=${this.buyTogetherId}&btRoomInit=true`
      // history[fromType === 'store' ? 'replace' : 'push'](`/buyTogetherCart/${this.buyTogetherId}`)
      this.setOpen()
    }
  }

  /** 从roomKey获取eatType */
  getEatTypeFromId () {
    return this.buyTogetherId?.split('_')[2] as TEatType
  }

  /** 拼单购物车点击前往点餐 */
  onSelectMenu () {
    const history = CashierController.history()
    this.syncCartFormMenuProductNo = ''
    this.setUseStatus(TEnumUserStatus.menu)
    multiPersonMobx.setCurrentPersonStatus(TEnumUserStatus.menu)
    this.saveCartFormMenu()
    history.push('/menu')
  }

  /** 菜单点击我选好了 */
  onSelectSuccess () {
    // 拼单成员可先返回购物车继续补选；必选餐品仍在后续结算时统一校验。
    if (this.checkAddCart()) return
    const history = CashierController.history()
    const hideBack = history.getPrevState() !== '/buyTogetherCart'
    this.setUseStatus(TEnumUserStatus.success)
    multiPersonMobx.setCurrentPersonStatus(TEnumUserStatus.success)
    setTimeout(() => {
      this.setMenuCartItemForCart()
    }, 100)
    if (hideBack) {
      history.replace('/buyTogetherCart')
    } else {
      history.goBack()
    }
  }

  /** 当前用户购物车临时数据用于菜单初始化 */
  saveCartFormMenu () {
    const cartItems = cartMobx.viewUserCartItems[multiPersonMobx.currentPersonInfo.personId] || []
    this.cartFormMenu = cartItems
  }

  /** 把当前菜单临时购物车加入拼单购物车 */
  setMenuCartItemForCart () {
    let listMap = cartMobx.viewUserCartItems
    let cartList = []
    // 合并之前的购物车重新change
    for (let key in listMap) {
      if (key !== multiPersonMobx.currentPersonInfo.personId) {
        cartList = concat(cartList, listMap[key])
      }
    }
    cartMobx.setCartItems([...cartList, ...this.cartFormMenu] as ICartItem[])
  }

  /** 菜单选中餐品 */
  onChangeProduct (type: 'add' | 'minus', product: IDoProduct) {
    let doProduct
    if (product.type === 'product') {
      doProduct = doProductMobx.doProduct
    } else if (['FixedCombo', 'OptionalCombo'].includes(product.type)) {
      doProduct = doComboMobx.doCombo
    }
    let cnt = product.cnt
    if (type === 'minus') {
      cnt = -1
      this.minusCart(doProduct)
    } else {
      this.addCart(doProduct)
    }
    if (this.syncCartFormMenuProductNo === product.no) {
      this.setMenuCartItemForCart()
      this.syncCartFormMenuProductNo = ''
    }
  }

  /** 结算页直接加购前，以共享购物车中的本人餐品作为临时编辑基准。 */
  prepareCartFormMenuForCartItemChange (productNo: string) {
    this.saveCartFormMenu()
    this.syncCartFormMenuProductNo = productNo
  }

  /** 添加餐品到购物车 */
  addCart (doProduct: IDoProduct | IDoCombo): any {
    const type = doProduct.type
    const productNo = doProduct.no
    const scaleNo = doProduct.scaleNo
    const remark = doProduct.remark || ''
    const startTimeOffset = doProduct.startTimeOffset || 0
    const doScale = doProduct.scales.find((scale) => scale.no === scaleNo)
    const price = (doScale && doScale.price) || 0
    const swapBuyProducts = doProduct.swapBuyProducts?.length ? doProduct.swapBuyProducts : []
    const timestamp = doProduct.timestamp
    const startTime = doProduct.startTime
    const pickTimeRange = doProduct.pickTimeRange
    const salesCnt = doProduct.salesCnt
    if (!scaleNo) {
      console.error('scale id is null')
      return TSaveCartError.DATA_ERROR
    }
    // 套餐
    const productGroups = (doProduct as IDoCombo).productGroups || undefined
    // 有pk则认为是一个购物车的修改
    const pk = doProduct.pk
    const isTogo = this.eatType !== 'dinein' ? 1 : 0
    const isCanTogo = doProduct.isCanTogo
    const isSingleSale = doProduct.isSingleSale
    const modifierGroups = convertDoModifierGroupsToModifierGroups(doProduct.modifierGroups)
    const addUserId = doProduct.addUserId || restaurantConfig?.member?.key || ''
    const addUserName = doProduct.addUserName || restaurantConfig?.member?.nickName || ''
    const addUserAvatar = doProduct.addUserAvatar || restaurantConfig?.member?.avatar || ''
    const fkey = createFkey({ productNo, scaleNo, modifierGroups, addUserId, isTogo, startTime, isSeat: doProduct.seat || '-1', isOrderSwapBuy: doProduct.isOrderSwapBuy, productGroups })
    const isTaxFree = (doProduct as IDoProduct).isTaxFree || false
    // 手机端自动合并
    if (restaurantConfig.app === 'mobile') {
      const index = this.cartFormMenu.findIndex(item => item.fkey === fkey)
      const cartItem = this.cartFormMenu[index]
      if (cartItem) {
        cartItem.cnt = cartItem.cnt + doProduct.cnt
        this.cartFormMenu[index] = cartItem
      } else {
        const newCartItem = createCartItem({ type, productNo, price, fkey, scaleNo, modifierGroups, productGroups, isTogo, isCanTogo, isSingleSale, cnt: doProduct.cnt, remark, startTimeOffset, addUserId, addUserName, addUserAvatar, isTaxFree, swapBuyProducts, timestamp, startTime, pickTimeRange, salesCnt }) as IViewCartItem
        this.cartFormMenu.push(newCartItem)
      }
    }
  }

  /** 当前用户菜单商品加入临时购物车 */
  minusCart (cart: IDoProduct | IDoCombo) {
    let cartItem = findLast(this.cartFormMenu, item => item.productNo === (cart as IDoProduct).no)
    if (cartItem) {
      // 减餐品低于起售数，就直接清空
      if (cartItem.salesCnt && cartItem.cnt <= cartItem.salesCnt) {
        this.remove(cartItem)
        return
      }
      if (cartItem.cnt <= 0) {
        this.remove(cartItem)
        return
      }
      cartItem.cnt--
      if (!cartItem.cnt) {
        const index = this.cartFormMenu.findIndex(item => item.productNo === cart.no && item.scaleNo === cart.scaleNo)
        if (~index) {
          this.cartFormMenu.splice(index, 1)
        }
      } else {
      }
    }
  }

  /** 当前用户点单状态 未点单/点单中/已完成 */
  setUseStatus (status: string) {
    this.useStatus = status
  }

  remove (cartItem: IViewCartItem) {
    this.removeCartItemFromMenu(cartItem.pk)
  }

  /** 从拼单菜单临时购物车精确删除当前编辑项，避免误删同餐品的其他规格或加料组合。 */
  removeCartItemFromMenu (pk?: string) {
    if (!pk) return
    const index = this.cartFormMenu.findIndex(item => item.pk === pk)
    if (~index) {
      this.cartFormMenu.splice(index, 1)
    }
  }

  /** 获取拼单多人购物车数据 */
  async getMultiCart () {
    const res = await MultiPersonOrderingAPI.getMultiCart({ storeId: 0, tableCode: 0, roomKey: Config.buyTogetherId })
    runInAction(() => {
      if (res && !res.error) {
        multiPersonOrderingMobx.setMultiPersonData(res)
      }
    })
  }

  /** 账单用户列表 */
  async getBulkAvatars (memberIds: number[], channel?: 'wechatminiapp' | 'wechat') {
    const res: any = await MoApi.getBulkAvatars(memberIds, channel)
    if (res && !res.error) {
      this.memberMap = keyBy(res.result, 'memberId')
    }
    return res.error ? res : res.result
  }

  /** 设置是否自动打开拼单弹层 */
  setOpen (open: boolean = false) {
    this.isOpen = open
  }

  /** 拼单数据存储本地 */
  setStorage = (data) => {
    localStorage.setItem(`ls.buyTogether.info.cache.${Config.BRANDID}`, JSON.stringify(data))
  }

  /** 拼单数据获取本地存储 */
  getStorage () {
    let data = null
    try {
      data = JSON.parse(localStorage.getItem(`ls.buyTogether.info.cache.${Config.BRANDID}`))
      if (data && data.buyTogetherId) {
        const btTime = data.buyTogetherId.split('_')[3]
        const nowTime = ddayjs().valueOf()
        // 超过三天就删除并返回空
        if ((nowTime - btTime) / 1000 / 60 / 60 / 24 > 3) {
          console.log('拼单数据超过三天了，被清除')
          this.delStorage()
          return null
        }
      }
    } catch (error) {}
    return data
  }

  /** 删除拼单本地存储 */
  delStorage () {
    localStorage.removeItem(`ls.buyTogether.info.cache.${Config.BRANDID}`)
  }

  returnMenuPage (targetType: string, delCache: boolean) {
    let url = `/menu?ct=brand&b=${restaurantConfig.brandId}&s=${restaurantConfig.storeId || 0}`
    if (targetType) {
      url += `&tt=${targetType}`
    }
    localStorage.setItem(`ls.needOpenScreen.${Config.BRANDID}`, 'false')
    if (delCache) {
      this.delStorage()
    }
    location.href = url
    // history.pushState('', '', '/menu')
  }
}
const BuyTogetherMobx = new BuyTogether()

autorun(() => {
  if (themeMobx?.brandTemplateDetails?.homeDecorates) {
    BuyTogetherMobx.setEntries()
  }
})

export default BuyTogetherMobx
